<template>
  <Head title="Two-factor authentication" />
  <div
    class="min-vh-100 d-flex align-items-center justify-content-center bg-light"
  >
    <DContainer class="py-5">
      <DRow class="justify-content-center">
        <DCol sm="10" md="8" lg="6" xl="4">
          <DCard>
            <h3 class="text-center mb-2">Two-factor authentication</h3>
            <p class="text-muted text-center small mb-4">
              {{
                usingRecoveryCode
                  ? "Enter one of your recovery codes."
                  : "Enter the 6-digit code from your authenticator app."
              }}
            </p>

            <DXForm
              v-if="!usingRecoveryCode"
              :key="'code'"
              :form="codeForm"
              submit-text="Verify"
              submit-loading-text="Verifying..."
              @submit="submit(codeForm)"
            />

            <DXForm
              v-else
              :key="'recovery'"
              :form="recoveryForm"
              submit-text="Verify"
              submit-loading-text="Verifying..."
              @submit="submit(recoveryForm)"
            />

            <div class="text-center mt-3">
              <DButton variant="link" size="sm" @click="toggleMode">
                {{
                  usingRecoveryCode
                    ? "Use an authenticator code instead"
                    : "Lost your device? Use a recovery code"
                }}
              </DButton>
            </div>
          </DCard>
        </DCol>
      </DRow>
    </DContainer>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { Head } from "@inertiajs/vue3";
import {
  defineForm,
  DXForm,
  DContainer,
  DRow,
  DCol,
  DCard,
  DButton,
} from "@omnitend/dashboard-for-laravel";

const usingRecoveryCode = ref(false);

// Fortify's POST /two-factor-challenge accepts `code` (TOTP) or `recovery_code`;
// both are nullable server-side and a falsy one is simply skipped, so one form
// carrying both would also work. Two forms is a UX choice, not a protocol one:
// each field can then be genuinely `required`, and the user is never staring at
// an empty box they're not supposed to fill in.
const codeForm = defineForm([
  {
    key: "code",
    type: "text",
    label: "Authentication code",
    placeholder: "123456",
    required: true,
    default: "",
    autocomplete: "one-time-code",
    inputmode: "numeric",
  },
] as const);

const recoveryForm = defineForm([
  {
    key: "recovery_code",
    type: "text",
    label: "Recovery code",
    placeholder: "xxxxxxxxxx-xxxxxxxxxx",
    required: true,
    default: "",
    autocomplete: "one-time-code",
  },
] as const);

const toggleMode = () => {
  usingRecoveryCode.value = !usingRecoveryCode.value;
  codeForm.form.reset();
  recoveryForm.form.reset();
  codeForm.form.clearErrors();
  recoveryForm.form.clearErrors();
};

const submit = async (form: typeof codeForm | typeof recoveryForm) => {
  try {
    await form.form.post("/two-factor-challenge");

    // Same reasoning as Login.vue: Fortify answers a JSON challenge with 204 and
    // nothing navigates on its own. Completing the challenge regenerates the
    // session (rotating CSRF), so this must be a hard navigation.
    window.location.href = "/sites";
  } catch {
    // 422 (bad code) — dfl has set form.errors and DXForm renders them.
    // 429 means the two-factor rate limiter tripped; dfl surfaces its message.
  }
};
</script>
